/**
 * Construye el patch que se aplica a una propuesta cuando UNA de las
 * dos partes hace clic en "Confirmar Intercambio".
 *
 * Modelo bilateral:
 *   - `completedBy`: array de uids que ya confirmaron. Comienza vacío,
 *     crece a 1 cuando una parte confirma, a 2 cuando confirman ambas.
 *   - `status`: queda en 'pending' hasta que AMBAS partes confirmen.
 *     Solo cuando completedBy.length >= 2 pasa a 'completed'.
 *
 * Esto evita los dos problemas del modelo anterior:
 *   1. Antes: cuando A confirmaba, el reconcile ponía status='cancelled'
 *      y B veía la propuesta desaparecer con un toast ambiguo de
 *      "ya no está pendiente". B no sabía que A había completado, ni
 *      que B también tenía que confirmar para que SU álbum se
 *      actualizara.
 *   2. Antes: el auto-release effect liberaba la reserva de B sin más,
 *      pero las láminas físicamente ya las había entregado B. Quedaba
 *      en un estado raro donde duplicates seguía decreasing-pending y
 *      collected sin actualizar.
 *
 * Ahora:
 *   - Cuando A completa: completedBy=[A.uid], status='pending', mensaje
 *     "⏳ Esperando confirmación del otro lado".
 *   - B sigue viendo la propuesta en su lista (sigue 'pending'), pero
 *     con un banner que dice "A confirmó — confirma tu lado".
 *   - Cuando B también completa: completedBy=[A.uid, B.uid],
 *     status='completed', el doc puede archivarse o borrarse después.
 */

export function buildCompletedByPatch(prop, completingUid, completerName) {
  const prevCompleted = Array.isArray(prop?.completedBy) ? prop.completedBy : [];
  // dedup por si la misma parte clickea dos veces
  const completedBy = Array.from(new Set([...prevCompleted, completingUid]));
  const bothCompleted = completedBy.length >= 2;

  const nameLabel = completerName || 'El otro coleccionista';

  return {
    completedBy,
    status: bothCompleted ? 'completed' : 'pending',
    modifiedBySystem: true,
    systemMessage: bothCompleted
      ? `✅ Intercambio confirmado por ambas partes — el doc puede archivarse.`
      : `✅ ${nameLabel} confirmó el intercambio. Ve a Amigos → ese coleccionista y confirma tu lado para actualizar tu álbum.`,
    // Limpiamos las marcas de "se retiraron láminas" porque no aplican
    // a una confirmación de trato — solo aplican cuando el reconcile
    // del proposal-sync recorta listas por intercambios paralelos.
    removedReceive: [],
    removedGive: [],
  };
}

/**
 * Indica si una propuesta está "esperando MI confirmación":
 * la otra parte ya completó pero yo no.
 *
 * Útil para banners en UI: "[Otro] confirmó — confirma tu lado".
 */
export function isAwaitingMyCompletion(prop, myUid) {
  if (!prop || !myUid) return false;
  if (prop.status !== 'pending') return false;
  const completed = Array.isArray(prop.completedBy) ? prop.completedBy : [];
  if (completed.length === 0) return false;       // nadie ha completado
  if (completed.includes(myUid)) return false;     // YO ya completé
  // El otro lado tiene que ser parte legítima del trato
  const otherUid = prop.fromUid === myUid ? prop.toUid : prop.fromUid;
  return completed.includes(otherUid);
}

/**
 * Indica si una propuesta ya completé YO pero no la otra parte.
 *
 * Útil para mostrar "tu lado: ✅ confirmado · esperando a [Otro]" en
 * el propio dispositivo del que ya confirmó.
 */
export function isAwaitingOtherCompletion(prop, myUid) {
  if (!prop || !myUid) return false;
  if (prop.status !== 'pending') return false;
  const completed = Array.isArray(prop.completedBy) ? prop.completedBy : [];
  if (!completed.includes(myUid)) return false;
  const otherUid = prop.fromUid === myUid ? prop.toUid : prop.fromUid;
  return !completed.includes(otherUid);
}
